import React, { useState, useEffect } from 'react';
import {
  useExternalStoreRuntime,
  AssistantRuntimeProvider,
} from "@assistant-ui/react";
import { chatService } from '../services/api';
import { ModelBuildToolUI } from '../components/chat/ModelBuildToolUI';
import { DashboardBuildToolUI } from '../components/chat/DashboardBuildToolUI';

const CHAT_STORAGE_KEY = 'modelCreationChatHistory';

// Session-aware state manager
class ChatStateManager {
  constructor() {
    this.listeners = new Set();
    this.messages = [];
    this.isRunning = false;
    this.runtimeKey = 0;
    this.currentSessionId = null;
    this.sessionTitle = "New Chat";
    this.isLoading = false;
    this.blockingBuildId = null;
    // Per-session state: {sessionId: {buildActive, autoMode, pendingQuestionId}}
    this.sessionStates = new Map();
  }

  // Get session state (creates default if doesn't exist)
  getSessionState(sessionId) {
    if (!sessionId) return { buildActive: false, autoMode: false, pendingQuestionId: null, buildId: null };

    if (!this.sessionStates.has(sessionId)) {
      this.sessionStates.set(sessionId, {
        buildActive: false,
        autoMode: false,
        pendingQuestionId: null,
        buildId: null
      });
    }
    return this.sessionStates.get(sessionId);
  }

  // Update session state
  updateSessionState(sessionId, updates) {
    if (!sessionId) return;

    const currentState = this.getSessionState(sessionId);
    this.sessionStates.set(sessionId, { ...currentState, ...updates });
    this.notifyListeners();
  }

  // Load session from API
  async loadSession(sessionId) {
    if (!sessionId) return false;

    console.log('[DEBUG-CHAT] 🔄 loadSession called for sessionId:', sessionId);
    try {
      this.isLoading = true;
      this.notifyListeners();

      const response = await chatService.getChatSession(sessionId);
      if (response && response.session) {
        this.currentSessionId = sessionId;
        this.sessionTitle = response.session.session_title;

        // Convert API messages to Assistant-UI format
        this.messages = (response.messages || []).map(msg => {
          let content = [];

          // Add rich content parts (tool calls, reasoning) if available
          if (msg.metadata?.rich_content && Array.isArray(msg.metadata.rich_content)) {
            // Filter out incomplete initiate_model_build tool calls
            content = msg.metadata.rich_content.filter(part => {
              if (part.type === 'tool-call' && part.toolName === 'initiate_model_build') {
                // Only show completed model build tools
                return part.status?.type === 'complete';
              }
              return true;
            });
          }

          // Always add the text content at the end
          content.push({ type: "text", text: msg.content });

          return {
            role: msg.role,
            content: content,
            id: `msg_${msg.message_id}`,
          };
        });

        // Initialize session state from backend
        const autoMode = response.session.auto_mode || false;
        const buildActive = response.session.build_active || false;
        const buildIds = response.session.build_ids || [];
        const buildId = buildIds.length > 0 ? buildIds[buildIds.length - 1] : null;
        this.updateSessionState(sessionId, { autoMode, buildActive, buildId });

        // Set active session in WebSocket
        chatService.setActiveSession(sessionId);

        this.isLoading = false;
        this.notifyListeners();
        console.log('[DEBUG-CHAT] ✅ Session loaded successfully:', sessionId, 'with', this.messages.length, 'messages');
        return true;
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      this.isLoading = false;
      this.notifyListeners();
    }
    return false;
  }

  // Create new session via API
  async createSession(title = null) {
    try {
      console.log('[DEBUG-CHAT] ✨ NEW SESSION CREATION STARTED, title:', title);
      this.isLoading = true;
      this.notifyListeners();
      
      const sessionTitle = title || null; // Let backend generate title after first message
      const response = await chatService.createChatSession(sessionTitle, "general_chat");
      console.log('[DEBUG-CHAT] Backend response received:', response);
      
      if (response && response.session_id) {
        this.currentSessionId = response.session_id;
        this.sessionTitle = response.session_title || sessionTitle;
        this.messages = [];
        this.saveCurrentSessionId();
        
        this.isLoading = false;
        this.notifyListeners();
        console.log('[DEBUG-CHAT] ✅ NEW SESSION CREATED successfully, sessionId:', this.currentSessionId);
        return this.currentSessionId;
      }
    } catch (error) {
      console.error('[DEBUG-CHAT] Failed to create session:', error);
      this.isLoading = false;
      this.notifyListeners();
    }
    return null;
  }

  // Get or create session
  async getOrCreateSession(sessionId = null) {
    // If sessionId provided, try to load it
    if (sessionId && sessionId !== this.currentSessionId) {
      const loaded = await this.loadSession(sessionId);
      if (loaded) return sessionId;
    }
    
    // If already have a current session, use it
    if (this.currentSessionId) {
      return this.currentSessionId;
    }
    
    // Try to load from storage first
    const savedSessionId = this.loadCurrentSessionId();
    if (savedSessionId) {
      const loaded = await this.loadSession(savedSessionId);
      if (loaded) return savedSessionId;
    }
    
    // Create new session
    return await this.createSession();
  }

  // Save current session ID to localStorage for persistence
  saveCurrentSessionId() {
    try {
      if (this.currentSessionId) {
        localStorage.setItem(`${CHAT_STORAGE_KEY}_current_session`, this.currentSessionId);
      }
    } catch (error) {
      console.warn('Failed to save current session ID:', error);
    }
  }

  // Load current session ID from localStorage
  loadCurrentSessionId() {
    try {
      return localStorage.getItem(`${CHAT_STORAGE_KEY}_current_session`);
    } catch (error) {
      console.warn('Failed to load current session ID:', error);
    }
    return null;
  }

  // Generate session title from first message
  generateSessionTitle() {
    const now = new Date();
    return `Chat ${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  addMessage(message) {
    this.messages = [...this.messages, message];
    this.notifyListeners();
  }

  setRunning(running) {
    this.isRunning = running;
    this.notifyListeners();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notifyListeners() {
    const sessionState = this.getSessionState(this.currentSessionId);
    this.listeners.forEach(listener => listener({
      messages: this.messages,
      isRunning: this.isRunning,
      runtimeKey: this.runtimeKey,
      currentSessionId: this.currentSessionId,
      sessionTitle: this.sessionTitle,
      isLoading: this.isLoading,
      blockingBuildId: this.blockingBuildId,
      sessionState: sessionState
    }));
  }

  getSnapshot() {
    const sessionState = this.getSessionState(this.currentSessionId);
    return {
      messages: this.messages,
      isRunning: this.isRunning,
      runtimeKey: this.runtimeKey,
      currentSessionId: this.currentSessionId,
      sessionTitle: this.sessionTitle,
      isLoading: this.isLoading,
      blockingBuildId: this.blockingBuildId,
      sessionState: sessionState
    };
  }

  setBlockingBuild(buildId) {
    this.blockingBuildId = buildId;
    this.notifyListeners();
  }

  async clearMessages() {
    // Create a new session instead of clearing current one
    await this.createSession();
  }

  async switchSession(sessionId) {
    console.log('[DEBUG-CHAT] 🔀 switchSession called, from:', this.currentSessionId, 'to:', sessionId);
    if (sessionId !== this.currentSessionId) {
      // Clean up old session state when switching (unless build is active)
      if (this.currentSessionId) {
        const oldState = this.sessionStates.get(this.currentSessionId);
        // Only delete if no active build - preserve state for sessions with ongoing builds
        if (oldState && !oldState.buildActive) {
          this.sessionStates.delete(this.currentSessionId);
          console.log(`[DEBUG-CHAT] Cleaned up session state for ${this.currentSessionId}`);
        }
      }

      await this.loadSession(sessionId);
      this.runtimeKey++; // Force runtime reset for new session
      this.notifyListeners();
      console.log('[DEBUG-CHAT] ✅ Session switched successfully to:', sessionId);
    } else {
      console.log('[DEBUG-CHAT] ℹ️ Already on session:', sessionId, '(no switch needed, but notifying listeners)');
      this.notifyListeners(); // Still notify listeners even if no switch, so UI can update
    }
  }

  // Send message to the current or specified session
  async sendMessage(text, sessionId = null) {
    try {
      // Use current session if none specified
      const targetSessionId = sessionId || this.currentSessionId;
      
      if (!targetSessionId) {
        throw new Error('No active session to send message to');
      }

      // Store reference to trigger runtime after navigation
      // Don't add message to state here - onNew() will handle it
      this.pendingMessage = text;
      
      return true;
      
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }

  // Get and clear any pending message that should be processed by the runtime
  getPendingMessage() {
    const message = this.pendingMessage;
    this.pendingMessage = null;
    return message;
  }

  setPendingQuestionId(questionId) {
    if (this.currentSessionId) {
      this.updateSessionState(this.currentSessionId, { pendingQuestionId: questionId });
    }
  }

  getPendingQuestionId() {
    if (!this.currentSessionId) return null;

    const sessionState = this.getSessionState(this.currentSessionId);
    const questionId = sessionState.pendingQuestionId;

    // Clear the pending question after retrieval
    this.updateSessionState(this.currentSessionId, { pendingQuestionId: null });

    return questionId;
  }

  handleOutboundMessage(messageData) {
    // Check for lifecycle events (build_started, build_complete) from model builder
    const eventType = messageData.metadata?.event_type;

    if (eventType === 'build_complete') {
      // Handle build completion - update state and mark tool as complete
      this.updateSessionState(this.currentSessionId, { buildActive: false });

      // Find and mark the initiate_model_build tool as complete in persisted messages
      this.messages = this.messages.map(msg => {
        if (msg.role === 'assistant' && msg.content) {
          const updatedContent = msg.content.map(part => {
            if (part.type === 'tool-call' && part.toolName === 'initiate_model_build') {
              return {...part, status: {type: 'complete'}};
            }
            return part;
          });
          return {...msg, content: updatedContent};
        }
        return msg;
      });

      this.notifyListeners();
      console.log('Received build_complete event from model builder, marked tool complete and unlocked input');
      // Don't add lifecycle event as a chat message
      return;
    }

    if (eventType === 'build_started') {
      // Handle build started event and store build_id
      const buildId = messageData.metadata?.build_id;
      this.updateSessionState(this.currentSessionId, { buildActive: true, buildId });
      console.log('Received build_started event from model builder, locking input');
      // Don't add lifecycle event as a chat message
      return;
    }

    // Extract question_id and awaiting_response from metadata
    const questionId = messageData.metadata?.question_id;
    const awaitingResponse = messageData.metadata?.awaiting_response === true;

    // Only set pendingQuestionId if this message is awaiting a response
    if (questionId && awaitingResponse) {
      this.setPendingQuestionId(questionId);
      console.log('Question received, unlocking input for user response');

      // Notify user of incoming question
      this.notifyUserOfQuestion();
    }

    // Extract message content
    let messageText = '';
    if (messageData.content && Array.isArray(messageData.content)) {
      for (const part of messageData.content) {
        if (part?.type === 'text') {
          messageText += part.text;
        }
      }
    }

    // Add as complete assistant message
    const outboundMessage = {
      role: 'assistant',
      content: [{ type: 'text', text: messageText }],
      id: `outbound-${Date.now()}`,
    };

    this.addMessage(outboundMessage);
    console.log('Added outbound message to chat', {questionId, awaitingResponse, messageText: messageText.substring(0, 50)});
  }

  async clearSession() {
    console.log('[DEBUG-CHAT] 🗑️ clearSession called, clearing sessionId:', this.currentSessionId);
    // Clear session state if we have a current session
    if (this.currentSessionId) {
      this.sessionStates.delete(this.currentSessionId);
    }

    this.currentSessionId = null;
    this.messages = [];
    this.sessionTitle = "New Chat";
    try {
      localStorage.removeItem(`${CHAT_STORAGE_KEY}_current_session`);
    } catch (error) {
      console.warn('Failed to clear session from storage:', error);
    }
    this.notifyListeners();
    console.log('[DEBUG-CHAT] ✅ Session cleared successfully');
  }

  notifyUserOfQuestion() {
    // Phase 1: Tab title notification (if window not focused)
    if (document.hidden) {
      const originalTitle = document.title;
      document.title = '⚠️ Question from Model Builder';

      // Reset title when user returns to tab
      const resetTitle = () => {
        if (!document.hidden) {
          document.title = originalTitle;
          document.removeEventListener('visibilitychange', resetTitle);
        }
      };
      document.addEventListener('visibilitychange', resetTitle);
    }

    // Sound notification (if enabled in preferences)
    const soundEnabled = localStorage.getItem('notificationSound') !== 'false'; // Default to true
    if (soundEnabled) {
      try {
        // Create a gentle notification beep using Web Audio API (no audio file needed)
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Configure sound: gentle notification tone
        oscillator.frequency.value = 800; // Hz
        oscillator.type = 'sine';
        gainNode.gain.value = 0.1; // Quiet volume

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1); // Short beep (100ms)
      } catch (e) {
        // Audio may be blocked or unsupported - fail silently
        console.log('Notification sound blocked:', e);
      }
    }
  }
}

// Global state manager instance
const globalChatState = new ChatStateManager();

// Convert our message format to assistant-ui format
const convertMessage = (message) => {
  return {
    role: message.role,
    content: message.content,
  };
};

export const ChatStateProvider = ({ children, formState, onFormUpdate, onArtifactCreated, sessionId }) => {
  const [state, setState] = useState(() => globalChatState.getSnapshot());

  // Subscribe to global state changes
  useEffect(() => {
    return globalChatState.subscribe(setState);
  }, []);

  // Register outbound message handler
  useEffect(() => {
    chatService.setOutboundMessageHandler((messageData) => {
      globalChatState.handleOutboundMessage(messageData);
    });

    return () => {
      chatService.setOutboundMessageHandler(null);
    };
  }, []);

  // Initialize or switch session when sessionId prop changes
  useEffect(() => {
    const initializeSession = async () => {
      console.log('[DEBUG-CHAT] 🚀 ChatStateProvider initialization, sessionId prop:', sessionId);
      if (sessionId) {
        console.log('[DEBUG-CHAT] Session ID provided, switching to session:', sessionId);
        await globalChatState.switchSession(sessionId);
      } else {
        console.log('[DEBUG-CHAT] No session ID provided, waiting for user to send first message');
      }
      // Don't auto-create session - let onNew() create it when user sends first message
    };

    initializeSession();
  }, [sessionId]);

  // Process any pending message after session is loaded
  useEffect(() => {
    if (state.currentSessionId && !state.isLoading) {
      const pendingMessage = globalChatState.getPendingMessage();
      if (pendingMessage) {
        // Trigger the message processing with a small delay
        setTimeout(() => {
          const messageObject = {
            content: [{ type: "text", text: pendingMessage }]
          };
          
          onNew(messageObject);
        }, 500); // Allow time for component to fully mount
      }
    }
  }, [state.currentSessionId, state.isLoading]);

  // Expose form state to the chat adapter
  useEffect(() => {
    window.modelCreationState = formState;
  }, [formState]);

  // Listen for auto-mode toggle events from widget
  useEffect(() => {
    const handleAutoModeChange = (event) => {
      const { toolCallId, autoMode } = event.detail;
      if (autoMode && globalChatState.blockingBuildId === toolCallId) {
        globalChatState.setBlockingBuild(null);
      }
    };

    window.addEventListener('model-build-auto-mode', handleAutoModeChange);
    return () => window.removeEventListener('model-build-auto-mode', handleAutoModeChange);
  }, []);

  const onNew = async (message) => {
    if (message.content[0]?.type !== "text")
      throw new Error("Only text messages are supported");

    const input = message.content[0].text;
    
    // Check if we have a session, if not create one
    if (!globalChatState.currentSessionId) {
      console.log('[DEBUG-CHAT] ⚠️ No active session detected, creating new session before sending message...');
      const newSessionId = await globalChatState.createSession();
      console.log('[DEBUG-CHAT] ✅ Session created for first message, ID:', newSessionId);
      
      if (!newSessionId) {
        throw new Error('Failed to create session');
      }
      
      // Update URL to include session parameter
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('session', newSessionId);
      window.history.pushState({}, '', currentUrl);
      console.log('[DEBUG-CHAT] 🔗 Updated URL with session parameter:', newSessionId);
    }
    
    const userMessage = {
      role: "user",
      content: [{ type: "text", text: input }],
      id: `user-${Date.now()}`,
    };

    globalChatState.addMessage(userMessage);
    globalChatState.setRunning(true);

    const assistantId = `assistant-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    let currentTools = new Map(); // Track tool executions: toolId -> { name, args, status, result }
    let thinkingLogs = [];
    let finalMessage = '';
    let accumulatedThinking = ''; // Accumulate LLM thinking stream
    // Only using markdown detection now for simplicity
    let artifactsProcessed = false; // Prevent processing same final message multiple times
    let modelBuildToolId = null; // Track the model build tool for special lifecycle handling
    
    const assistantMessage = {
      role: "assistant",
      content: [],
      id: assistantId,
    };
    
    globalChatState.addMessage(assistantMessage);

    const updateMessage = () => {
      const content = [];
      
      // Add reasoning content part (proper @assistant-ui/react format)
      // Always show reasoning section while processing, even if no logs yet
      if (!finalMessage || thinkingLogs.length > 0) {
        content.push({ 
          type: "reasoning",
          text: thinkingLogs.join('\n'),
          status: {
            type: finalMessage ? "complete" : "running"
          }
        });
      }
      
      // Add main message content (only show final message, not streaming intermediate)
      if (finalMessage) {
        content.push({ type: "text", text: finalMessage });
      } else {
        // Don't show fallback text - let the reasoning animation indicate thinking
        content.push({ type: "text", text: "" });
      }
      
      // Add tool executions as tool-call content parts (with safe defaults)
      for (const [toolId, toolData] of currentTools.entries()) {
        content.push({
          type: "tool-call",
          toolName: toolData.name || "unknown_tool",
          toolCallId: toolId,
          args: toolData.args || {},
          result: toolData.result || null,
          status: {
            type: toolData.status || "running"
          },
          argsText: JSON.stringify(toolData.args || {})
        });
      }
      
      globalChatState.messages = globalChatState.messages.map(m => 
        m.id === assistantId 
          ? { ...m, content }
          : m
      );
      globalChatState.notifyListeners();
    };
    
    // Legacy visualization polling removed - now handled via direct S3 URL detection

    try {
      // Get or create persistent session
      const sessionId = await globalChatState.getOrCreateSession();

      // Buffer for incomplete JSON chunks (large messages may be split)
      let jsonBuffer = '';

      const handlers = {
        onChunk: (chunk) => {
          if (typeof chunk === 'string' && chunk.trim()) {
            // Append to buffer
            jsonBuffer += chunk.trim();

            // Safety: Prevent buffer from growing indefinitely (max 10MB)
            if (jsonBuffer.length > 10 * 1024 * 1024) {
              console.error('JSON buffer exceeded 10MB - clearing to prevent memory issues');
              jsonBuffer = '';
              return;
            }

            try {
              // Try to parse the accumulated buffer
              const data = JSON.parse(jsonBuffer);

              // Success! Clear the buffer and process the message
              jsonBuffer = '';
              
              // Removed tool result and stream detection - using only markdown detection for simplicity
              
              if (data.event === 'message_stream') {
                // Handle streaming message content (LLM thinking) - accumulate chunks
                const streamContent = data.data;

                // Accumulate streaming thinking content
                if (streamContent) {
                  accumulatedThinking += streamContent;

                  // Find or create the LLM thinking entry
                  const thinkingIndex = thinkingLogs.findIndex(log => log === 'Agent reasoning' || log.startsWith('💭'));

                  if (thinkingIndex >= 0) {
                    // Update existing thinking entry
                    thinkingLogs[thinkingIndex] = '💭 ' + accumulatedThinking.trim();
                  } else {
                    // Create new thinking entry
                    thinkingLogs.push('💭 ' + accumulatedThinking.trim());
                  }
                  updateMessage();
                }

              } else if (data.event === 'tool_call_status') {
                // Handle tool status updates (for model builds)
                const { toolCallId, statusMessage, needsInput, autoModeEnabled } = data.data;

                // Find and update the tool
                for (const [id, toolData] of currentTools.entries()) {
                  if (id === toolCallId) {
                    toolData.statusMessage = statusMessage;
                    toolData.needsInput = needsInput;

                    // Set blocking if needs input and not in auto mode
                    if (needsInput && !autoModeEnabled) {
                      globalChatState.setBlockingBuild(toolCallId);
                    } else if (globalChatState.blockingBuildId === toolCallId) {
                      globalChatState.setBlockingBuild(null);
                    }
                    break;
                  }
                }
                updateMessage();

              } else if (data.event === 'tool_result') {
                // Handle tool result from backend
                const { tool_name, result } = data.data;

                // Find the tool and update its result
                for (const [toolId, toolData] of currentTools.entries()) {
                  if (toolData.name === tool_name) {
                    toolData.result = result;

                    // For initiate_model_build, also store build_id in session state
                    if (tool_name === 'initiate_model_build' && result?.build_id) {
                      globalChatState.updateSessionState(sessionId, { buildId: result.build_id });
                    }
                    break;
                  }
                }
                updateMessage();

              } else if (data.event === 'streaming_update') {
                // Handle "thinking" updates and tool execution
                const updateText = data.data;

                // Reset accumulated thinking when new tool starts
                if (updateText.startsWith('Executing:')) {
                  accumulatedThinking = '';
                }

                // Add to thinking logs (avoid duplicates)
                if (!thinkingLogs.includes(updateText)) {
                  thinkingLogs.push(updateText);
                }

                // Track tool executions
                if (updateText.startsWith('Executing:')) {
                  // Extract tool name: "Executing: list_datasets"
                  const toolName = updateText.replace('Executing: ', '');

                  // For initiate_model_build, remove any existing instances (keep only latest)
                  if (toolName === 'initiate_model_build') {
                    // Remove old initiate_model_build tools to avoid duplicates
                    for (const [id, data] of currentTools.entries()) {
                      if (data.name === 'initiate_model_build') {
                        currentTools.delete(id);
                      }
                    }
                    globalChatState.updateSessionState(sessionId, { buildActive: true });
                  }

                  const toolId = `${toolName}_${Date.now()}`;

                  // Track tool ID for initiate_model_build
                  if (toolName === 'initiate_model_build') {
                    modelBuildToolId = toolId;
                  }

                  currentTools.set(toolId, {
                    name: toolName,
                    args: {},
                    status: 'running',
                    result: null
                  });

                } else if (updateText.startsWith('Completed:')) {
                  const completedToolName = updateText.replace('Completed: ', '');

                  // Special case: don't mark initiate_model_build as complete yet
                  // It will be marked complete when build_complete event arrives
                  if (completedToolName === 'initiate_model_build') {
                    console.log('initiate_model_build tool finished, but keeping UI running until build completes');
                    // Don't mark as complete - wait for build_complete event
                  } else {
                    // Mark other tools as complete normally
                    for (const [, toolData] of currentTools.entries()) {
                      if (toolData.status === 'running') {
                        toolData.status = 'complete';
                        break;
                      }
                    }
                  }
                }

                // Always update display for real-time thinking updates
                updateMessage();

              } else if (data.event === 'build_complete') {
                // Handle build completion event from model builder
                globalChatState.updateSessionState(sessionId, { buildActive: false });

                // Mark the model build tool as complete
                if (modelBuildToolId && currentTools.has(modelBuildToolId)) {
                  currentTools.get(modelBuildToolId).status = 'complete';
                  console.log('Model build completed, marking tool as complete and unlocking input');
                  updateMessage();
                }

              } else if (data.event === 'message') {
                // Handle final message with structured visualization URLs
                
                if (data.data && typeof data.data === 'object') {
                  // Handle structured message format from backend
                  if (data.data.content && Array.isArray(data.data.content)) {
                    // Reconstruct the full message text from content parts
                    let fullMessageText = '';
                    for (const part of data.data.content) {
                      if (part && typeof part === 'object' && part.type === 'text' && part.text) {
                        fullMessageText += part.text;
                      }
                    }
                    
                    if (fullMessageText) {
                      finalMessage = fullMessageText;

                      // Deduplicate: Remove final message text from thinking logs
                      const thinkingIndex = thinkingLogs.findIndex(log => log.startsWith('💭'));
                      if (thinkingIndex >= 0) {
                        const thinkingContent = thinkingLogs[thinkingIndex].substring(2).trim(); // Remove "💭 "

                        // Normalize whitespace for comparison
                        const normalizedThinking = thinkingContent.replace(/\s+/g, ' ').trim();
                        const normalizedFinal = fullMessageText.replace(/\s+/g, ' ').trim();

                        // Check if thinking ends with the final message (most common case)
                        if (normalizedThinking.endsWith(normalizedFinal)) {
                          // Calculate where the duplicate starts in the normalized string
                          const duplicateStartPos = normalizedThinking.length - normalizedFinal.length;

                          // Use a simple heuristic: find roughly where this position is in the original string
                          // by counting non-whitespace characters
                          let charCount = 0;
                          let originalPos = 0;
                          for (let i = 0; i < thinkingContent.length; i++) {
                            if (!/\s/.test(thinkingContent[i])) {
                              charCount++;
                            }
                            if (charCount >= duplicateStartPos) {
                              originalPos = i;
                              break;
                            }
                          }

                          // Remove from that position onwards
                          const deduplicatedThinking = thinkingContent.substring(0, originalPos).trim();

                          // If nothing remains, remove the entire thinking entry
                          if (!deduplicatedThinking) {
                            thinkingLogs.splice(thinkingIndex, 1);
                          } else {
                            // Update with deduplicated content
                            thinkingLogs[thinkingIndex] = '💭 ' + deduplicatedThinking;
                          }
                        } else if (normalizedThinking === normalizedFinal) {
                          // Exact match - remove the entire thinking entry
                          thinkingLogs.splice(thinkingIndex, 1);
                        }
                      }

                      // Handle model creation detection FIRST - check multiple possible fields
                      let modelId = null;
                      if (data.data.model_id) {
                        modelId = data.data.model_id;
                      } else if (data.data.build_id) {
                        modelId = data.data.build_id;
                      } else if (data.data.experiment_id) {
                        modelId = data.data.experiment_id;
                      } else {
                        // Try to extract build/model ID from the message text
                        const buildIdMatch = fullMessageText.match(/(?:build_id|model_id|Build ID|Model ID)[:\s]*[`"']?([a-f0-9\-]{36})[`"']?/i);
                        if (buildIdMatch) {
                          modelId = buildIdMatch[1];
                        }
                      }
                      
                      // Model creation callback removed
                      
                      // Handle artifacts only once
                      if (!artifactsProcessed) {
                        artifactsProcessed = true;

                        // Handle structured visualization URLs
                        if (data.data.visualization_urls && Array.isArray(data.data.visualization_urls)) {
                          data.data.visualization_urls.forEach((url, index) => {
                            if (onArtifactCreated) {
                              onArtifactCreated({
                                id: `viz_${Date.now()}_${index}`,
                                url: url,
                                title: `Data Visualization ${index + 1}`,
                                type: 'Chart',
                                description: 'Generated visualization from your data analysis'
                              });
                            }
                          });
                        }

                        // Handle table data from query results
                        if (data.data.table_data && Array.isArray(data.data.table_data)) {
                          data.data.table_data.forEach((tableData, index) => {
                            if (onArtifactCreated) {
                              onArtifactCreated({
                                id: `table_${Date.now()}_${index}`,
                                data: tableData,
                                title: `Data Table ${index + 1}`,
                                type: 'Table',
                                description: 'Query results from your data analysis'
                              });
                            }
                          });
                        }

                        // Handle checkpoint data
                        // Try multiple paths since tool result structure varies
                        console.log('=== Checking for checkpoint data ===');
                        console.log('Full data object:', JSON.stringify(data, null, 2));
                        console.log('data.data:', data.data);
                        console.log('data.data keys:', data.data ? Object.keys(data.data) : 'null');

                        const checkpointData =
                          data.data?._checkpoint_data ||
                          data._checkpoint_data ||
                          (typeof data.data === 'string' ? JSON.parse(data.data)?._checkpoint_data : null);

                        console.log('Extracted checkpoint data:', checkpointData);

                        if (checkpointData) {
                          console.log('✅ Detected checkpoint artifact! Creating artifact...', checkpointData);
                          if (onArtifactCreated) {
                            onArtifactCreated({
                              id: `checkpoint_${Date.now()}`,
                              data: checkpointData,
                              title: `${checkpointData.phase?.replace('_', ' ')} Checkpoint` || 'Model Checkpoint',
                              type: 'Checkpoint',
                              description: `Checkpoint with ${checkpointData.total_objects} saved objects`
                            });
                            console.log('✅ Checkpoint artifact created!');
                          }
                        } else {
                          console.log('❌ No checkpoint data found');
                          console.log('Available keys in data.data:', data.data ? Object.keys(data.data) : 'N/A');
                        }
                      }
                      
                      updateMessage();
                    }
                  }
                } else if (data.data && Array.isArray(data.data)) {
                  // Fallback: Handle legacy array format for backward compatibility
                  let fullMessageText = '';
                  for (const part of data.data) {
                    if (part && typeof part === 'object' && part.type === 'text' && part.text) {
                      fullMessageText += part.text;
                    }
                  }
                  
                  if (fullMessageText) {
                    finalMessage = fullMessageText;
                    updateMessage();
                  }
                }
                
              } else if (data.status === 'complete') {
                // Handle completion signal
              }

            } catch (parseError) {
              // JSON parsing failed - likely an incomplete chunk
              // Keep the buffer and wait for more data
              // Only log as raw text if it doesn't look like JSON at all
              if (!jsonBuffer.startsWith('{') && !jsonBuffer.startsWith('[')) {
                // Not JSON - treat as raw text and clear buffer
                thinkingLogs.push(`📝 ${chunk.trim()}`);
                jsonBuffer = '';
                updateMessage();
              }
              // Otherwise, keep buffering (don't clear jsonBuffer)
            }
          }
        },
        
        onComplete: async () => {
          console.log('[SESSION-TITLE] 🎯 Agent response completed, starting cleanup');
          
          // Log any remaining buffered JSON (incomplete message)
          if (jsonBuffer.length > 0) {
            console.warn('Stream ended with incomplete JSON in buffer:', jsonBuffer.substring(0, 200) + '...');
            jsonBuffer = '';
          }

          // Ensure all running tools are marked as complete
          // EXCEPT initiate_model_build which completes when build_complete event arrives
          for (const [, toolData] of currentTools.entries()) {
            if (toolData.status === 'running' && toolData.name !== 'initiate_model_build') {
              toolData.status = 'complete';
            }
          }

          // If we have a final message, update one last time
          if (finalMessage) {
            updateMessage();
          } else {
            // Fallback if something went wrong
            finalMessage = 'Response completed';
            if (thinkingLogs.length > 0) {
              finalMessage = `Processing completed.\n\nExecuted ${currentTools.size} tools.`;
            }
            updateMessage();
          }

          globalChatState.setRunning(false);
          globalChatState.notifyListeners();

          console.log('[SESSION-TITLE] 🔄 Fetching updated session title for sessionId:', sessionId);
          
          // Refresh session title after response completes
          try {
            const response = await chatService.getChatSessions();
            console.log('[SESSION-TITLE] ✅ Got sessions response, total sessions:', response.sessions?.length);
            
            const currentSession = response.sessions?.find(s => s.session_id === sessionId);
            console.log('[SESSION-TITLE] 🔍 Found current session:', currentSession ? 'YES' : 'NO');
            
            if (currentSession) {
              console.log('[SESSION-TITLE] 📝 Old title:', globalChatState.sessionTitle);
              console.log('[SESSION-TITLE] 📝 New title:', currentSession.session_title);
              
              if (currentSession.session_title) {
                globalChatState.sessionTitle = currentSession.session_title;
                globalChatState.notifyListeners();
                console.log('[SESSION-TITLE] ✅ Session title updated and listeners notified');
              } else {
                console.warn('[SESSION-TITLE] ⚠️ New session title is empty/null');
              }
            } else {
              console.warn('[SESSION-TITLE] ⚠️ Current session not found in sessions list');
            }
          } catch (error) {
            console.error('[SESSION-TITLE] ❌ Failed to refresh session title:', error);
          }
        },
        
        onError: (error) => {
          console.error('WebSocket streaming error:', error);
          globalChatState.messages = globalChatState.messages.map(m => 
            m.id === assistantId 
              ? { ...m, content: [{ type: "text", text: `❌ Error: ${error}` }] }
              : m
          );
          globalChatState.saveToStorage();
          globalChatState.notifyListeners();
          globalChatState.setRunning(false);
        }
      };

      // Get pending question_id if answering a question
      const questionId = globalChatState.getPendingQuestionId();

      // Start streaming
      await chatService.sendMessageStreaming(input, sessionId, questionId, handlers);
      
    } catch (error) {
      console.error('Chat error:', error);
      globalChatState.messages = globalChatState.messages.map(m => 
        m.id === assistantId 
          ? { ...m, content: [{ type: "text", text: "Sorry, I encountered an error. Please try again." }] }
          : m
      );
      globalChatState.saveToStorage();
      globalChatState.notifyListeners();
      globalChatState.setRunning(false);
    }
  };

  const runtime = useExternalStoreRuntime({
    isRunning: state.isRunning,
    messages: state.messages,
    convertMessage,
    onNew,
  });

  return (
    <AssistantRuntimeProvider key={state.runtimeKey} runtime={runtime}>
      {children}
      <ModelBuildToolUI />
      <DashboardBuildToolUI />
    </AssistantRuntimeProvider>
  );
};

export { globalChatState };